import { FeatureFlagsContext } from '@tableau/data-catalog-shared';
import { KeySet } from '@tableau/data-structures';
import * as React from 'react';
import { Query } from 'react-apollo';
import {
  DatabaseLineageCountsFilteredByTableQuery,
  DatabaseLineageCountsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseLineageCountsFilteredByTableQuery';
import {
  DatabaseLineageCountsQuery,
  DatabaseLineageCountsQueryVariables,
} from '../../../gen-src/queries/DatabaseLineageCountsQuery';
import { DatabaseLineageCountsFilteredByTableQueryGql } from '../../queries/DatabaseLineageCountsFilteredByTableQueryGql';
import { DatabaseLineageCountsQueryGql } from '../../queries/DatabaseLineageCountsQueryGql';
import { LineageCountsParentProps } from '../lineage/LineageCountsCacher';
import { renderFilteredLineageCounts, renderUnfilteredLineageCounts } from '../lineage/LineageCountsFunctions';

export type DatabaseLineageCounts = {
  readonly filteredTableName: string | null;
  readonly upstreamDatabases: number;
  readonly upstreamTables: number;
  readonly upstreamFlows: number;
  readonly upstreamDatasources: number;
  readonly tables: number;
  readonly downstreamDashboards: number;
  readonly downstreamDatabases: number;
  readonly downstreamTables: number;
  readonly downstreamVirtualConnections: number;
  readonly downstreamVirtualConnectionTables: number;
  readonly downstreamFlows: number;
  readonly downstreamDatasources: number;
  readonly downstreamWorkbooks: number;
  readonly downstreamSheets: number;
  readonly downstreamMetrics: number;
  readonly downstreamOwners: number;
};

function createUnfilteredCounts(data: DatabaseLineageCountsQuery): DatabaseLineageCounts | null {
  const database = data.databases && data.databases[0];
  if (database
    && database.unfilteredTablesConnection
    && database.upstreamDatabasesConnection
    && database.upstreamTablesConnection
    && database.upstreamFlowsConnection
    && database.upstreamDatasourcesConnection
    && database.downstreamDashboardsConnection
    && database.downstreamDatabasesConnection
    && database.downstreamTablesConnection
    && database.downstreamFlowsConnection
    && database.downstreamDatasourcesConnection
    && database.downstreamWorkbooksConnection
    && database.downstreamSheetsConnection
    && database.downstreamMetricsConnection
    && database.downstreamOwnersConnection
  ) {
    return {
      filteredTableName: null,
      tables: database.unfilteredTablesConnection.totalCount,
      upstreamDatabases: database.upstreamDatabasesConnection.totalCount,
      upstreamTables: database.upstreamTablesConnection.totalCount,
      upstreamFlows: database.upstreamFlowsConnection.totalCount,
      upstreamDatasources: database.upstreamDatasourcesConnection.totalCount,
      downstreamDashboards: database.downstreamDashboardsConnection.totalCount,
      downstreamDatabases: database.downstreamDatabasesConnection.totalCount,
      downstreamTables: database.downstreamTablesConnection.totalCount,
      downstreamVirtualConnections: database.downstreamVirtualConnectionsConnection?.totalCount || 0,
      downstreamVirtualConnectionTables: database.downstreamVirtualConnectionTablesConnection?.totalCount || 0,
      downstreamFlows: database.downstreamFlowsConnection.totalCount,
      downstreamDatasources: database.downstreamDatasourcesConnection.totalCount,
      downstreamWorkbooks: database.downstreamWorkbooksConnection.totalCount,
      downstreamSheets: database.downstreamSheetsConnection.totalCount,
      downstreamMetrics: database.downstreamMetricsConnection.totalCount,
      downstreamOwners: database.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

function createFilteredCounts(data: DatabaseLineageCountsFilteredByTableQuery): DatabaseLineageCounts | null {
  const database = data.databases && data.databases[0];
  const filteredTable = database && database.filteredTablesConnection && database.filteredTablesConnection.nodes[0];
  if (database
    && database.unfilteredTablesConnection
    && filteredTable
    && filteredTable.upstreamDatabasesConnection
    && filteredTable.upstreamTablesConnection
    && filteredTable.upstreamFlowsConnection
    && filteredTable.upstreamDatasourcesConnection
    && filteredTable.downstreamDashboardsConnection
    && filteredTable.downstreamDatabasesConnection
    && filteredTable.downstreamTablesConnection
    && filteredTable.downstreamFlowsConnection
    && filteredTable.downstreamDatasourcesConnection
    && filteredTable.downstreamWorkbooksConnection
    && filteredTable.downstreamSheetsConnection
    && filteredTable.downstreamMetricsConnection
    && filteredTable.downstreamOwnersConnection
  ) {
    return {
      filteredTableName: filteredTable?.name ?? null,
      tables: database.unfilteredTablesConnection.totalCount,
      upstreamDatabases: filteredTable.upstreamDatabasesConnection.totalCount,
      upstreamTables: filteredTable.upstreamTablesConnection.totalCount,
      upstreamFlows: filteredTable.upstreamFlowsConnection.totalCount,
      upstreamDatasources: filteredTable.upstreamDatasourcesConnection.totalCount,
      downstreamDashboards: filteredTable.downstreamDashboardsConnection.totalCount,
      downstreamDatabases: filteredTable.downstreamDatabasesConnection.totalCount,
      downstreamTables: filteredTable.downstreamTablesConnection.totalCount,
      downstreamVirtualConnections: filteredTable.downstreamVirtualConnectionsConnection?.totalCount || 0,
      downstreamVirtualConnectionTables: filteredTable.downstreamVirtualConnectionTablesConnection?.totalCount || 0,
      downstreamFlows: filteredTable.downstreamFlowsConnection.totalCount,
      downstreamDatasources: filteredTable.downstreamDatasourcesConnection.totalCount,
      downstreamWorkbooks: filteredTable.downstreamWorkbooksConnection.totalCount,
      downstreamSheets: filteredTable.downstreamSheetsConnection.totalCount,
      downstreamMetrics: filteredTable.downstreamMetricsConnection.totalCount,
      downstreamOwners: filteredTable.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

export type DatabaseLineageCountsQueryWrapperProps = {
  readonly databaseId: string;
  readonly selectedTableKeys: KeySet;
} & LineageCountsParentProps<DatabaseLineageCounts>;

export const DatabaseLineageCountsQueryWrapper = (props: DatabaseLineageCountsQueryWrapperProps) => {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);
  const selectedTables = Object.keys(props.selectedTableKeys);
  if (selectedTables.length > 0) {
    const selectedTableId = selectedTables[0];
    return (
      <Query<DatabaseLineageCountsFilteredByTableQuery, DatabaseLineageCountsFilteredByTableQueryVariables>
        query={DatabaseLineageCountsFilteredByTableQueryGql}
        variables={{ databaseId: props.databaseId, tableId: selectedTableId, publishedConnectionsLineageEnabled }}
      >
        {result => renderFilteredLineageCounts(result, createUnfilteredCounts, createFilteredCounts, props.children)}
      </Query>
    );
  } else {
    return (
      <Query<DatabaseLineageCountsQuery, DatabaseLineageCountsQueryVariables>
        query={DatabaseLineageCountsQueryGql}
        variables={{ databaseId: props.databaseId, publishedConnectionsLineageEnabled }}
      >
        {result => renderUnfilteredLineageCounts(result, createUnfilteredCounts, props.children)}
      </Query>
    );
  }
}
